import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import fMessage from '@/components/fMessage';
import { FetchNodesAction } from '@/models/nodes';
import type {
  ChangeAction,
  OnMount_SettingPage_Action,
  OnUnmount_SettingPage_Action,
  OnClick_Setting_StartEdit_Action,
  OnClick_Setting_CancelEdit_Action,
  OnClick_Setting_SaveEdit_Action,
  OnClick_Setting_SaveIntroduction_Action,
  OnChange_Setting_Cover_Action,
  OnChange_Setting_CoverImage_Action,
  OnChange_Setting_ExhibitMatchTags_Action,
  OnChange_Setting_Title_Action,
  OnChange_Setting_Introduction_Action,
  OnChange_Setting_Limitation_Action,
  OnChange_Setting_NodeLimitationMessage_Action,
  SettingEditModule,
} from './types';
import { settingInitStates } from './initialState';

function getEditInitPayload(
  module: SettingEditModule,
  nodeManagerPage: ConnectState['nodeManagerPage'],
): Partial<ConnectState['nodeManagerPage']> {
  const editingModules = nodeManagerPage.setting_editingModules.includes(module)
    ? nodeManagerPage.setting_editingModules
    : [...nodeManagerPage.setting_editingModules, module];
  const base = {
    setting_editingModules: editingModules,
    setting_state: 'editing' as const,
  };

  if (module === 'title') {
    return {
      ...base,
      setting_nodeTitle: nodeManagerPage.setting_nodeInfo.title,
      setting_nodeTitle_Error: '',
    };
  }

  return {
    ...base,
    setting_nodeLimitation: nodeManagerPage.setting_nodeInfo.limitation,
    setting_nodeLimitationMessage: nodeManagerPage.setting_nodeInfo.limitationMessage,
  };
}

export const settingEffects = {
  *onMount_SettingPage({}: OnMount_SettingPage_Action, { select, call, put }: EffectsCommandMap) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );

    const params: Parameters<typeof FServiceAPI.Node.details>[0] = {
      nodeId: nodeManagerPage.nodeId,
    };

    const {
      data: data_nodeDetails,
    }: {
      data: {
        nodeLogo?: string | null;
        nodeId: number;
        nodeName: string;
        nodeDomain: string;
        nodeTitle: string;
        nodeShortDescription: string;
        status: 1 | 2 | 3;
        nodeSuspendInfo: string;
        coverImage?: string;
        exhibitMatchTags?: string[];
      };
    } = yield call(FServiceAPI.Node.details, params);
    const coverImage = data_nodeDetails.coverImage ?? '';
    const exhibitMatchTags = data_nodeDetails.exhibitMatchTags ?? [];
    const logoFromApi = String(data_nodeDetails.nodeLogo ?? '').trim();
    const nodeLogo =
      logoFromApi ||
      (nodeManagerPage.nodeId === data_nodeDetails.nodeId
        ? String(nodeManagerPage.nodeCover || '').trim()
        : '');
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeID: data_nodeDetails.nodeId,
        setting_nodeName: data_nodeDetails.nodeName,
        setting_nodeCover: nodeLogo,
        setting_nodeTitle: data_nodeDetails.nodeName || '',
        setting_nodeUrl: FUtil.Format.completeUrlByDomain(data_nodeDetails.nodeDomain).replace(
          /http(s)?:\/\//,
          '',
        ),
        setting_nodeInfo: {
          cover: nodeLogo,
          title: data_nodeDetails.nodeTitle || '',
          introduction: data_nodeDetails.nodeShortDescription || '',
          limitation:
            data_nodeDetails.status === 1
              ? 'public'
              : data_nodeDetails.status === 2
              ? 'private'
              : 'pause',
          limitationMessage: data_nodeDetails.nodeSuspendInfo || '',
          coverImage,
          exhibitMatchTags: [...exhibitMatchTags],
        },
        setting_coverImage: coverImage,
        setting_exhibitMatchTags: [...exhibitMatchTags],
        setting_pageState: 'loaded',
      },
    });
  },
  *onUnmount_SettingPage({}: OnUnmount_SettingPage_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        ...settingInitStates,
      },
    });
  },
  *onClick_Setting_StartEdit(
    { payload }: OnClick_Setting_StartEdit_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );

    if (nodeManagerPage.setting_editingModules.includes(payload.module)) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: getEditInitPayload(payload.module, nodeManagerPage),
    });
  },
  *onClick_Setting_CancelEdit(
    { payload }: OnClick_Setting_CancelEdit_Action,
    { select, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );
    const { module } = payload;
    if (!nodeManagerPage.setting_editingModules.includes(module)) {
      return;
    }

    const nextEditingModules = nodeManagerPage.setting_editingModules.filter((m) => m !== module);
    const resetPayload: Partial<ConnectState['nodeManagerPage']> = {
      setting_editingModules: nextEditingModules,
      setting_state: nextEditingModules.length === 0 ? 'normal' : 'editing',
    };

    if (module === 'title') {
      resetPayload.setting_nodeTitle = nodeManagerPage.setting_nodeInfo.title;
      resetPayload.setting_nodeTitle_Error = '';
    } else if (module === 'visibility') {
      resetPayload.setting_nodeLimitation = nodeManagerPage.setting_nodeInfo.limitation;
      resetPayload.setting_nodeLimitationMessage =
        nodeManagerPage.setting_nodeInfo.limitationMessage;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: resetPayload,
    });
  },
  *onClick_Setting_SaveEdit(
    { payload }: OnClick_Setting_SaveEdit_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );

    const { module } = payload;
    const nodeId = nodeManagerPage.setting_nodeID;
    const params: Parameters<typeof FServiceAPI.Node.setNodeInfo>[0] = { nodeId };
    let nextNodeInfo = { ...nodeManagerPage.setting_nodeInfo };

    if (module === 'title') {
      const nodeTitle = nodeManagerPage.setting_nodeTitle.trim();
      if (nodeTitle === '') {
        fMessage(FI18n.i18nNext.t('naming_convention_node_title_required'), 'error');
        return;
      }
      if (nodeManagerPage.setting_nodeTitle_Error !== '') {
        return;
      }
      params.nodeTitle = nodeTitle;
      nextNodeInfo = { ...nextNodeInfo, title: nodeTitle };
    } else if (module === 'visibility') {
      params.status =
        nodeManagerPage.setting_nodeLimitation === 'public'
          ? 1
          : nodeManagerPage.setting_nodeLimitation === 'private'
          ? 2
          : 8;
      params.nodeSuspendInfo = nodeManagerPage.setting_nodeLimitationMessage.trim();
      nextNodeInfo = {
        ...nextNodeInfo,
        limitation: nodeManagerPage.setting_nodeLimitation,
        limitationMessage: nodeManagerPage.setting_nodeLimitationMessage.trim(),
      };
    } else {
      return;
    }

    const { ret, errCode, msg } = yield call(FServiceAPI.Node.setNodeInfo, params);

    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }

    const nextEditingModules = nodeManagerPage.setting_editingModules.filter((m) => m !== module);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_editingModules: nextEditingModules,
        setting_state: nextEditingModules.length === 0 ? 'normal' : 'editing',
        setting_nodeInfo: nextNodeInfo,
        nodeTitle: module === 'title' ? nextNodeInfo.title : nodeManagerPage.nodeTitle,
        node_limitation:
          module === 'visibility' ? nextNodeInfo.limitation : nodeManagerPage.node_limitation,
      },
    });

    yield put<FetchNodesAction>({
      type: 'nodes/fetchNodes',
    });
  },
  *onChange_Setting_Cover(
    { payload }: OnChange_Setting_Cover_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );
    const nodeLogo = String(payload.value || '').trim();
    const { ret, errCode, msg } = yield call(FServiceAPI.Node.setNodeInfo, {
      nodeId: nodeManagerPage.setting_nodeID,
      nodeLogo,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeCover: nodeLogo,
        setting_nodeInfo: {
          ...nodeManagerPage.setting_nodeInfo,
          cover: nodeLogo,
        },
        nodeCover: nodeLogo,
      },
    });
    yield put<FetchNodesAction>({
      type: 'nodes/fetchNodes',
    });
  },
  *onClick_Setting_SaveIntroduction(
    { payload }: OnClick_Setting_SaveIntroduction_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );
    const intro = payload.value.trim();
    const { ret, errCode, msg } = yield call(FServiceAPI.Node.setNodeInfo, {
      nodeId: nodeManagerPage.setting_nodeID,
      nodeShortDescription: intro,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeIntroduction: intro,
        setting_nodeInfo: {
          ...nodeManagerPage.setting_nodeInfo,
          introduction: intro,
        },
      },
    });
  },
  *onChange_Setting_Title(
    { payload }: OnChange_Setting_Title_Action,
    { put }: EffectsCommandMap,
  ) {
    let setting_nodeTitle_Error: string = '';
    if (payload.value === '') {
      setting_nodeTitle_Error = FI18n.i18nNext.t('naming_convention_node_title_required');
    } else if (payload.value.length > 100) {
      setting_nodeTitle_Error = '不超过100个字符';
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeTitle: payload.value,
        setting_nodeTitle_Error: setting_nodeTitle_Error,
      },
    });
  },
  *onChange_Setting_Introduction(
    { payload }: OnChange_Setting_Introduction_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeIntroduction: payload.value,
      },
    });
  },
  *onChange_Setting_Limitation(
    { payload }: OnChange_Setting_Limitation_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeLimitation: payload.value,
      },
    });
  },
  *onChange_Setting_NodeLimitationMessage(
    { payload }: OnChange_Setting_NodeLimitationMessage_Action,
    { put }: EffectsCommandMap,
  ) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_nodeLimitationMessage: payload.value,
      },
    });
  },
  *onChange_Setting_CoverImage(
    { payload }: OnChange_Setting_CoverImage_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );
    const { ret, errCode, msg } = yield call(FServiceAPI.Node.setNodeInfo, {
      nodeId: nodeManagerPage.setting_nodeID,
      coverImage: payload.value,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_coverImage: payload.value,
        setting_nodeInfo: {
          ...nodeManagerPage.setting_nodeInfo,
          coverImage: payload.value,
        },
      },
    });
  },
  *onChange_Setting_ExhibitMatchTags(
    { payload }: OnChange_Setting_ExhibitMatchTags_Action,
    { select, call, put }: EffectsCommandMap,
  ) {
    const { nodeManagerPage }: ConnectState = yield select(
      ({ nodeManagerPage }: ConnectState) => ({
        nodeManagerPage,
      }),
    );
    const tags = [...payload.value];
    const { ret, errCode, msg } = yield call(FServiceAPI.Node.setNodeInfo, {
      nodeId: nodeManagerPage.setting_nodeID,
      exhibitMatchTags: tags,
    });
    if (ret !== 0 || errCode !== 0) {
      fMessage(msg, 'error');
      return;
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        setting_exhibitMatchTags: tags,
        setting_nodeInfo: {
          ...nodeManagerPage.setting_nodeInfo,
          exhibitMatchTags: tags,
        },
      },
    });
  },
};
